import { Elem } from '@goodt-wcore/elem';
import { setupDevtoolsPlugin } from '@vue/devtools-api';
import { AuthManager, StoreManager, EB } from '@goodt-wcore/managers';
import { createErrorInfoHandleStrategy, ErrorManager } from '@goodt-common/error-service';

const { useEventBus } = EB;
const { store, ValueObject } = StoreManager;

const STORE_STATE_INSPECTOR = '$store';
const DESCRIPTOR_PROPS_INSPECTOR = 'descriptor.props';
const DESCRIPTOR_VARS_INSPECTOR = 'descriptor.vars';
const DESCRIPTOR_EVENTS_INSPECTOR = 'descriptor.events';
const AUTH_LAYER = '$auth';
const STORE_LAYER = '$store';
const STORE_TIMELINE_ID = '$store';
const EVENTBUS_TIMELINE_ID = '$eventBus';
const ERRORS_TIMELINE_ID = 'errors';
const CORE_PROBLEMS_TIMELINE_ID = 'core';
const WIDGET_DEBUG_TIMELINE_ID = 'widget';

const isElem = ({ $options: { __name } = {} }) => __name === 'Elem';

/**
 *
 * @param {import('@goodt-wcore/managers/ErrorManager').ErrorInfo} errorInfo
 * @this {import('@goodt-wcore/managers/ErrorManager').ErrorInfoHandleStrategy}
 * @return {null|ErrorInfo}
 * @constructor
 */
const errorInfoHandler = function errorInfoHandler(errorInfo) {
    const { error } = errorInfo;
    if (error.isCancel) {
        return null;
    }

    return errorInfo;
};

const initErrorHandle = (app) => {
    const { $errorService } = app;

    const strategy = createErrorInfoHandleStrategy({ handler: errorInfoHandler });
    const errorManager = ErrorManager.create();
    errorManager.setStrategy(strategy);
    $errorService.subscribe((...args) => errorManager.handle(...args));

    return {
        errorManager
    };
};

const setupDevtools = (app) => {
    const { eventBus } = useEventBus();

    const { errorManager } = initErrorHandle(app);

    setupDevtoolsPlugin(
        {
            id: 'goodt-devtools-plugin',
            label: 'Goodt devtools plugin',
            packageName: 'goodt-devtools-plugin',
            homepage: 'https://goodt.me',
            app,
            enableEarlyProxy: true,
            componentStateTypes: [STORE_STATE_INSPECTOR, DESCRIPTOR_PROPS_INSPECTOR, DESCRIPTOR_VARS_INSPECTOR, DESCRIPTOR_EVENTS_INSPECTOR]
        },
        (api) => {
            // elem badge
            api.on.visitComponentTree(({ treeNode, componentInstance }, context) => {
                if (isElem(componentInstance)) {
                    const [pkg, name = pkg] = componentInstance.type.split('::');
                    // eslint-disable-next-line no-param-reassign
                    treeNode.name = name?.split('/').pop();
                    treeNode.tags.push({
                        label: 'elem',
                        textColor: 0xffffff,
                        backgroundColor: 0x1a67fe
                    });
                }
            });
            // $storeState inspector
            api.on.inspectComponent(({ instanceData, componentInstance }, context) => {
                if (!isElem(componentInstance)) {
                    return;
                }
                const { $storeState = {}, descriptor, props } = componentInstance;

                Object.entries($storeState).forEach(([key, value]) => {
                    instanceData.state.push({
                        type: STORE_STATE_INSPECTOR,
                        key,
                        value
                    });
                });
                Object.entries(descriptor.props).forEach(([key, value]) => {
                    instanceData.state.push({
                        type: DESCRIPTOR_PROPS_INSPECTOR,
                        key,
                        value: {
                            ...value,
                            type: value.type.name,
                        }
                    });
                });
                Object.entries(props.varAliases ?? {}).forEach(([key, value]) => {
                    instanceData.state.push({
                        type: DESCRIPTOR_VARS_INSPECTOR,
                        key,
                        value
                    });
                });
                Object.entries(descriptor.events ?? {}).forEach(([key, value]) => {
                    instanceData.state.push({
                        type: DESCRIPTOR_EVENTS_INSPECTOR,
                        key,
                        value
                    });
                });

            });

            // $store layer
            api.addInspector({
                id: STORE_LAYER,
                label: STORE_LAYER,
                icon: 'storage'
            });
            api.on.getInspectorTree((payload, context) => {
                if (payload.inspectorId === STORE_LAYER) {
                    payload.rootNodes = [{ id: STORE_LAYER, label: '$store' }];
                }
            });
            api.on.getInspectorState((payload, context) => {
                if (payload.inspectorId === STORE_LAYER && payload.nodeId === STORE_LAYER) {
                    payload.state = {
                        state: Object.entries(store.state).map(([key, value]) => ({
                            key,
                            value: ValueObject.getValue(value)
                        })),
                        'state.raw': Object.entries(store.state).map(([key, value]) => ({ key, value }))
                    };
                }
            });

            // $auth layer
            api.addInspector({
                id: AUTH_LAYER,
                label: AUTH_LAYER,
                icon: 'lock'
            });
            api.on.getInspectorTree((payload, context) => {
                if (payload.inspectorId === AUTH_LAYER) {
                    payload.rootNodes = [{ id: AUTH_LAYER, label: '$auth' }];
                }
            });
            api.on.getInspectorState(async (payload, context) => {
                if (payload.inspectorId === AUTH_LAYER && payload.nodeId === AUTH_LAYER) {
                    const {
                        instance: { adapter }
                    } = AuthManager;
                    if (!adapter) {
                        return;
                    }
                    const profile = await adapter.init().then(() => adapter.getUserProfile());
                    const { authenticated, config, token } = adapter;
                    payload.state = {
                        adapter: [
                            { key: 'authenticated', value: authenticated },
                            { key: 'config', value: config },
                            { key: 'token', value: token },
                            { key: 'profile', value: profile }
                        ]
                    };
                }
            });

            // $store timeline
            api.addTimelineLayer({
                id: STORE_TIMELINE_ID,
                color: 0x1a67fe,
                label: STORE_TIMELINE_ID
            });
            store.addCommitHandler((state, commitOptions = {}) => {
                const { context: { id: componentId, type: component } = {} } = commitOptions;
                api.addTimelineEvent({
                    layerId: STORE_TIMELINE_ID,
                    event: {
                        title: '$commit',
                        time: api.now(),
                        data: {
                            component: component != null ? `${component}[${componentId}]` : 'Unknown',
                            state
                        }
                    }
                });
                api.sendInspectorState(STORE_LAYER);
            });
            // $eventBus timeline
            api.addTimelineLayer({
                id: EVENTBUS_TIMELINE_ID,
                color: 0x1a67fe, // 0xff984f,
                label: '$eventBus'
            });
            eventBus.hook((event, data) => {
                const { context, type: eventType } = event;
                const { id: componentId, type: component } = context ?? {};
                api.addTimelineEvent({
                    layerId: EVENTBUS_TIMELINE_ID,
                    event: {
                        title: `${eventType} – ${component}`,
                        time: api.now(),
                        data: {
                            component: context != null ? `${component}[${componentId}]` : 'Unknown',
                            event: eventType,
                            data
                        }
                    }
                });
            });

            // core validations error and warnings timeline
            api.addTimelineLayer({
                id: CORE_PROBLEMS_TIMELINE_ID,
                color: 0xf16541, // 0xff984f,
                label: 'core'
            });

            // widget debug info timeline
            api.addTimelineLayer({
                id: WIDGET_DEBUG_TIMELINE_ID,
                color: 0xf5941c, // 0xff984f,
                label: 'widget: debug'
            });

            // rest errors timeline
            api.addTimelineLayer({
                id: ERRORS_TIMELINE_ID,
                color: 0xbf112e, // 0xff984f,
                label: 'errors'
            });

            errorManager.subscribe(({ error, context, scope, type }) => {
                const { elem: component, hook, id: componentId } = scope ?? {};
                let layerId;

                switch (type) {
                    case ErrorManager.ErrorInfoType.CoreError:
                    case ErrorManager.ErrorInfoType.CoreWarning:
                        layerId = CORE_PROBLEMS_TIMELINE_ID;
                        break;
                    case ErrorManager.ErrorInfoType.WidgetDebugMessage:
                        layerId = WIDGET_DEBUG_TIMELINE_ID;
                        break;
                    default:
                        layerId = ERRORS_TIMELINE_ID;
                }

                const { name, message, stack, ...restErrorProps } = error;
                api.addTimelineEvent({
                    layerId,
                    event: {
                        title: error.name,
                        time: api.now(),
                        data: {
                            error,
                            ...context && { component: context },
                            ...(scope && {
                                scope: {
                                    elem: component ? `${component}[${componentId}]` : scope,
                                    hook
                                }
                            }),
                            details: {
                                type,
                                ...restErrorProps
                            }
                        }
                    }
                });
            });
        }
    );
};

/**
 * Devtools plugin init
 */
export const useDevtools = (App, { isEnabled = false } = {}) => {
    if (process.env.NODE_ENV !== 'production' || !!__VUE_PROD_DEVTOOLS__ || isEnabled) {
        setupDevtools(App);
    }
};
